import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { CountUp } from '../motion/CountUp';
import { Reveal } from '../motion/Reveal';
import { StaggerGrid, StaggerItem } from '../motion/StaggerGrid';

interface KpiEntry {
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  source?: string;
}

interface KPICounterProps {
  eyebrow?: string;
  title?: string;
  stats: KpiEntry[];
}

export function KPICounter({
  eyebrow = 'Chiffres documentés',
  title = 'Chaque dossier est traçable.',
  stats,
}: KPICounterProps) {
  return (
    <section className="relative overflow-hidden border-y border-stone-200 bg-paper py-20 md:py-24">
      {/* Background subtil */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 dot-grid-masked opacity-50" />
      </div>

      <Container className="relative">
        <Reveal>
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur">
              <span className="live-dot" aria-hidden />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                {eyebrow}
              </span>
            </div>
            <h2 className="mt-5 font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-[-0.025em] text-balance text-ink">
              {title}
            </h2>
          </div>
        </Reveal>

        <StaggerGrid className="grid gap-px overflow-hidden rounded-2xl border border-stone-200 bg-stone-200 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <StaggerItem
              key={s.label}
              className="group/kpi relative bg-paper p-8 transition-colors hover:bg-stone-50/60"
            >
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                Métrique
              </p>
              <p className="mt-3 font-display text-[clamp(2.25rem,4vw,3rem)] font-bold leading-none tracking-[-0.035em] text-ink tabular-nums">
                {s.prefix}
                <CountUp end={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-4 text-[13.5px] font-medium leading-snug text-stone-700">
                {s.label}
              </p>
              {s.source ? (
                <p className="mt-2 font-mono text-[10.5px] text-stone-400">
                  Source, {s.source}
                </p>
              ) : null}
            </StaggerItem>
          ))}
        </StaggerGrid>
      </Container>
    </section>
  );
}

export const KPICounterConfig: ComponentConfig<KPICounterProps> = {
  fields: {
    eyebrow: { type: 'text' },
    title: { type: 'textarea' },
    stats: {
      type: 'array',
      arrayFields: {
        value: { type: 'number' },
        prefix: { type: 'text' },
        suffix: { type: 'text' },
        label: { type: 'text' },
        source: { type: 'text' },
      },
      defaultItemProps: { value: 0, prefix: '', suffix: '', label: '', source: '' },
    },
  },
  defaultProps: {
    eyebrow: 'Chiffres documentés',
    title: 'Chaque dossier est traçable.',
    stats: [
      { value: 0, suffix: '€', label: "d'avance demandée à l'artisan partenaire", source: 'CGV mandataire CEE' },
      { value: 9, suffix: ' ans', label: 'conservation des pièces du dossier CEE', source: 'Charte qualité officielle des CEE' },
      { value: 3, suffix: '', label: 'pôles intégrés (travaux, courtage énergie, mandataire CEE)', source: 'Solution Éco France' },
      { value: 7, suffix: '', label: 'audiences couvertes', source: 'shared/constants/audiences.ts' },
    ],
  },
  render: KPICounter,
};
